import { useEffect, useRef } from 'react'
import { Pause, Phone, Play, X } from 'lucide-react'
import { useWavesurfer } from '@wavesurfer/react'
import { cn } from '@/lib/utils'

interface Props {
  audioUrl: string
  darkMode: boolean
  onCollapse: () => void
  onCall: () => void
}

function fmt(s: number): string {
  if (!Number.isFinite(s) || s < 0) return '0:00'
  return `${Math.floor(s / 60)}:${Math.floor(s % 60)
    .toString()
    .padStart(2, '0')}`
}

/**
 * Mini-player inline waveform (wavesurfer) à l'intérieur d'une ligne voicemail.
 * Auto-play au mount. Boutons : play/pause, rappeler, fermer.
 */
export function InlineVoicemailPlayer({ audioUrl, darkMode, onCollapse, onCall }: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const startedRef = useRef(false)

  const { wavesurfer, isReady, isPlaying, currentTime } = useWavesurfer({
    container: containerRef,
    url: audioUrl,
    waveColor: darkMode ? 'rgba(251,146,60,0.3)' : 'rgba(251,146,60,0.5)',
    progressColor: 'rgb(249,115,22)',
    cursorColor: 'rgba(249,115,22,0.8)',
    cursorWidth: 1,
    barWidth: 2,
    barGap: 1,
    barRadius: 2,
    height: 28,
    normalize: true,
  })

  const duration = wavesurfer?.getDuration() ?? 0

  useEffect(() => {
    if (isReady && wavesurfer && !startedRef.current) {
      startedRef.current = true
      void wavesurfer.play()
    }
  }, [isReady, wavesurfer])

  useEffect(() => {
    return () => {
      wavesurfer?.pause()
    }
  }, [wavesurfer])

  return (
    <div
      className={cn(
        'flex items-center gap-2 mt-1.5 px-2 py-1.5 rounded-lg',
        darkMode ? 'bg-slate-800/60' : 'bg-orange-50/80',
      )}
    >
      <button
        type="button"
        onClick={() => (isReady ? wavesurfer?.playPause() : undefined)}
        disabled={!isReady}
        className={cn(
          'w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all disabled:opacity-40',
          isPlaying
            ? 'bg-orange-500 text-white'
            : darkMode
              ? 'bg-slate-700 text-slate-300 hover:bg-orange-500/20 hover:text-orange-400'
              : 'bg-orange-100 text-orange-600 hover:bg-orange-200',
        )}
      >
        {isPlaying ? <Pause className="w-2.5 h-2.5" /> : <Play className="w-2.5 h-2.5 ml-0.5" />}
      </button>

      <div className="flex-1 min-w-0 relative" style={{ height: 28 }}>
        {!isReady && (
          <div className="absolute inset-0 flex items-center gap-px">
            {Array.from({ length: 40 }).map((_, i) => (
              <div
                key={i}
                className={cn(
                  'flex-1 rounded',
                  darkMode ? 'bg-slate-700' : 'bg-orange-200',
                )}
                style={{ height: `${4 + Math.random() * 16}px`, opacity: 0.4 }}
              />
            ))}
          </div>
        )}
        <div
          ref={containerRef}
          style={{ display: isReady ? 'block' : 'none' }}
        />
      </div>

      <span
        className={cn(
          'text-[10px] tabular-nums flex-shrink-0',
          darkMode ? 'text-slate-500' : 'text-gray-400',
        )}
      >
        {fmt(currentTime)}/{fmt(duration)}
      </span>

      <button
        type="button"
        onClick={onCall}
        className={cn(
          'p-1 rounded-full transition-all flex-shrink-0',
          darkMode
            ? 'text-green-500 hover:text-green-400 hover:bg-green-900/30'
            : 'text-green-600 hover:text-green-700 hover:bg-green-50',
        )}
        title="Rappeler"
      >
        <Phone className="w-3 h-3" />
      </button>

      <button
        type="button"
        onClick={onCollapse}
        className={cn(
          'p-1 rounded-full transition-all flex-shrink-0',
          darkMode ? 'text-slate-600 hover:text-slate-400' : 'text-gray-300 hover:text-gray-500',
        )}
        title="Fermer"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  )
}
